<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            color="primary"
            x-small
            text
            @click="() => $router.push({ name: 'usuarios' })"
            :disabled="submittingForm"
          >
            <v-icon left> fa fa-angle-left </v-icon>
          </v-btn>
          {{ editing ? "Editando" : "Criando" }} Usuário
        </h1>
      </v-col>
    </v-row>
    <ValidationObserver v-slot="{ handleSubmit }">
      <form
        @submit.prevent="handleSubmit(submitForm)"
        autocomplete="off"
        :disabled="submittingForm"
      >
        <v-row>
          <v-col class="pt-0 pb-0" cols="6">
            <ValidationProvider name="Nome Completo" rules="required" v-slot="{ errors }">
              <e-label>Nome Completo</e-label>
              <v-text-field :error-messages="errors" v-model="form.name" dense solo />
            </ValidationProvider>
          </v-col>

          <v-col class="pt-0 pb-0" cols="6">
            <ValidationProvider name="Tipo" rules="required" v-slot="{ errors }">
              <e-label>Tipo</e-label>
              <e-autocomplete
                :items="rolesPermitidasNoTipo"
                :return-object="false"
                :item-value="(value) => value.id"
                :item-text="(text) => text.name"
                v-model="form.roles[0].id"
                @change="(value) => onRoleChange(value)"
                label="Selecione uma opção"
                dense
                solo
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>

          <!-- Campos de senha: exibidos na criação OU quando o tipo exige senha na edição -->
          <template v-if="deveExibirCamposSenha">
            <v-col class="pt-0 pb-0" cols="6">
              <ValidationProvider
                name="Senha"
                :rules="editing ? 'min:4|confirmed:password' : 'required|min:4'"
                v-slot="{ errors }"
                vid="password"
              >
                <e-label>Senha</e-label>
                <v-text-field
                  :error-messages="errors"
                  type="password"
                  autocomplete="off"
                  v-model="form.password"
                  dense
                  solo
                />
              </ValidationProvider>
            </v-col>
            <v-col class="pt-0 pb-0" cols="6">
              <ValidationProvider
                name="Confirme a senha"
                :rules="editing ? 'min:4|confirmed:password' : 'required|min:4|confirmed:password'"
                v-slot="{ errors }"
              >
                <e-label>Confirme a senha</e-label>
                <v-text-field
                  :error-messages="errors"
                  type="password"
                  v-model="form.password_confirm"
                  dense
                  solo
                />
              </ValidationProvider>
            </v-col>
          </template>

          <v-col class="pt-0 pb-0" cols="6">
            <ValidationProvider name="Login de acesso" rules="required" v-slot="{ errors }">
              <e-label>Login de acesso</e-label>
              <v-text-field
                :error-messages="errors"
                v-model="form.email"
                :label="tipoAtualEhProfessor ? 'CPF' : 'E-mail'"
                :hint="
                  deveExibirEmailDoProfessor && emailDoProfessor
                    ? 'E-mail preenchido automaticamente'
                    : ''
                "
                persistent-hint
                dense
                solo
              />
            </ValidationProvider>
          </v-col>

          <v-col class="pt-0 pb-0" cols="6">
            <e-label>Franquia(s) do usuário:</e-label>
            <e-autocomplete
              :items="franquiasList"
              :return-object="false"
              v-model="form.franquias_permitidas"
              :item-value="(franquia) => franquia.id"
              :item-text="(franquia) => franquia.descricao"
              :loading="carregandoFranquias"
              label="Selecione uma opção"
              dense
              multiple
              solo
            />
          </v-col>
          <v-col cols="12" v-if="editing && tipoAtualEhProfessor && !temProfessor">
            <v-alert type="warning" color="orange" dark prominent border="left" elevation="3">
              Para concluir a atualização do usuário, é necessário completar o cadastro na página de
              Professores.
              <br />
              Clique no botão “Enviar Dados e Redirecionar” para completar o cadastro.
            </v-alert>
          </v-col>

          <v-col cols="12" v-if="!tipoAtualEhProfessor || (tipoAtualEhProfessor && temProfessor)">
            <v-btn
              color="primary"
              :disabled="submittingForm"
              :loading="submittingForm"
              type="submit"
              block
            >
              Enviar Dados
            </v-btn>
          </v-col>

          <!-- Botão redirecionar: é professor SEM vínculo -->
          <v-col cols="12" v-if="tipoAtualEhProfessor && !temProfessor">
            <v-btn
              color="primary"
              :disabled="submittingForm"
              :loading="submittingForm"
              type="button"
              block
              @click="submitForm(true)"
            >
              Enviar Dados e Redirecionar
            </v-btn>
          </v-col>
        </v-row>
      </form>
    </ValidationObserver>
  </main-template>
</template>

<script>
import ELabel from "../../components/ELabel.vue";
import rolesService from "../../Services/RolesService";

const ROLES_COM_SENHA = ["direção", "coordenador", "secretario", "supervisor"];

const ROLE_PROFESSOR = "professor";

export default {
  components: { ELabel },

  props: {
    editing: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      submittingForm: false,
      form: { roles: [{}] },
      franquiasList: [],
      carregandoFranquias: false,
      outrosRolesList: [],
      rolesList: [],
      emailDoProfessor: null,
      cpfDoProfessor: null,
      emailOriginal: null,
      temProfessor: false,
    };
  },

  computed: {
    usuarioLogado() {
      return this.$store.getters["Auth/user"];
    },
    usuarioPodeVerProfessor() {
      const roles = this.usuarioLogado?.roles || [];

      return roles.some((role) => {
        const nome = String(role.name || "").toLowerCase();
        return nome === "super-admin" || nome === "ti";
      });
    },
    rolesPermitidasNoTipo() {
      return this.outrosRolesList.filter((role) => {
        const nome = String(role.name || "").toLowerCase();
        if (!this.editing && nome.includes("professor")) {
          return false;
        }
        if (this.editing && nome.includes("professor")) {
          return this.usuarioPodeVerProfessor;
        }
        return true;
      });
    },
    roleAtual() {
      const roleId = this.form.roles?.[0]?.id;
      if (!roleId) return null;
      const encontrado = this.outrosRolesList.find((r) => r.id === roleId);
      return encontrado || null;
    },
    tipoAtualEhProfessor() {
      if (!this.roleAtual) return false;
      const nome = (this.roleAtual.name || this.roleAtual.slug || "").toLowerCase();
      return nome.includes(ROLE_PROFESSOR);
    },
    tipoAtualExigeSenha() {
      if (!this.roleAtual) return false;
      const nome = (this.roleAtual.name || this.roleAtual.slug || "").toLowerCase();
      return ROLES_COM_SENHA.some((r) => nome.includes(r));
    },
    deveExibirCamposSenha() {
      return !this.tipoAtualEhProfessor;
    },
    deveExibirEmailDoProfessor() {
      return this.tipoAtualExigeSenha;
    },
    rolesParaCriacao() {
      return this.outrosRolesList.filter(
        (role) => !role.name.toLowerCase().includes(ROLE_PROFESSOR)
      );
    },
  },

  methods: {
    loginEhEmail(login) {
      return String(login || "").includes("@");
    },
    guardarEmailAtualSeValido() {
      if (this.loginEhEmail(this.form.email)) {
        this.emailOriginal = this.form.email;
      }
    },
    getLoginParaPerfilComSenha() {
      if (this.emailDoProfessor) {
        return this.emailDoProfessor;
      }

      if (this.loginEhEmail(this.emailOriginal)) {
        return this.emailOriginal;
      }

      return "";
    },
    async onRoleChange(roleId) {
      this.guardarEmailAtualSeValido();
      this.setRole(roleId);

      const roleObj = this.outrosRolesList.find((r) => r.id === roleId);
      if (!roleObj) return;

      const nomeRole = (roleObj.name || roleObj.slug || "").toLowerCase();
      const ehProfessor = nomeRole.includes(ROLE_PROFESSOR);

      const exigeSenha = ROLES_COM_SENHA.some((r) => nomeRole.includes(r));

      if (exigeSenha) {
        this.form.email = this.getLoginParaPerfilComSenha();
        this.form.password = "";
        this.form.password_confirm = "";
      } else if (ehProfessor) {
        this.form.email = this.cpfDoProfessor || "";
        this.form.password = "";
        this.form.password_confirm = "";
      }
    },

    setRole(item) {
      this.$set(this.form.roles, 0, { id: item });
    },

    async listarFranquias() {
      try {
        this.franquiasList = await this.$services.franquiasService.syncAll();
      } catch (error) {
        this.$handleError(error);
      }
    },

    async carregarRoles() {
      this.outrosRolesList = await rolesService.syncAll();
    },

    async pegarUsuario() {
      try {
        const { usuario_id } = this.$route.params;
        const response = await this.$services.usuariosService.getUsuario(usuario_id);

        this.temProfessor = !!response.professor;

        if (response?.professor?.email) {
          this.emailDoProfessor = response.professor.email;
        }

        if (response?.professor?.cpf) {
          this.cpfDoProfessor = response.professor.cpf;
        }

        return response;
      } catch (error) {
        return this.$handleError(error);
      }
    },

    async submitForm(redirecionarProfessor = false) {
      if (this.deveExibirCamposSenha && this.form.password !== this.form.password_confirm) {
        this.$toast.warn("As senhas não estão iguais");
        return;
      }

      this.form.especial = Boolean(this.form.especial);
      this.submittingForm = true;

      if (!this.form.password) delete this.form.password;

      try {
        let response;

        if (this.editing) {
          response = await this.$services.usuariosService.atualizarUsuario(this.form);
        } else {
          response = await this.$services.usuariosService.criarUsuario(this.form);
        }

        this.$toast.success("Usuário atualizado com sucesso");

        if (redirecionarProfessor) {
          const query = {
            user_id: this.form.id || response.id,
          };
          const emailParaProfessor = this.getLoginParaPerfilComSenha();

          if (emailParaProfessor) {
            query.email = emailParaProfessor;
          }

          this.$router.push({
            name: "professores.create",
            query,
          });
        } else {
          this.$router.push({ name: "usuarios" });
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.submittingForm = false;
      }
    },

    async loadData() {
      this.carregandoFranquias = true;
      try {
        this.$loaderService.open("Carregando dados");
        await Promise.all([this.carregarRoles(), this.listarFranquias()]);
        this.$loaderService.close();
        this.$forceUpdate();
        await this.loadFormData();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoFranquias = false;
      }
    },

    async loadFormData() {
      if (!this.editing) return;

      const usuario = await this.pegarUsuario();

      if (!usuario || this.form.id) return;

      this.form = { ...usuario };

      this.emailOriginal = this.form.email || null;

      if (this.deveExibirEmailDoProfessor && this.emailDoProfessor) {
        this.form.email = this.emailDoProfessor;
      }
    },
  },

  mounted() {
    this.loadData();
  },
};
</script>

<style></style>
